import PropTypes from 'prop-types';
import { useMemo } from 'react';
// material
import { alpha, ThemeProvider, createTheme, useTheme as useMuiTheme } from '@mui/material/styles';
// hooks
import useSettings from 'hooks/useSettings';
import useTheme from 'hooks/useTheme';
//
import componentsOverride from 'theme/overrides';

// ----------------------------------------------------------------------

ThemePrimaryColor.propTypes = {
  children: PropTypes.node
};

export default function ThemePrimaryColor({ children }) {
  const defaultTheme = useMuiTheme();
  const { setColor } = useSettings();
  const { activeTheme } = useTheme();

  // Helper function to ensure color values are strings
  const ensureStringColor = (color, fallback) => {
    if (typeof color === 'string') return color;
    if (Array.isArray(color)) return color[0];
    return fallback || '#FFFFFF';
  };

  // Use primary color from active theme if available, otherwise use settings
  const primaryColor = useMemo(() => {
    if (activeTheme?.config?.colors?.primary) {
      const themePrimary = activeTheme.config.colors.primary;
      // Ensure primary has the correct structure with all required properties
      return {
        lighter: ensureStringColor(themePrimary.lighter, '#CAFBF5'),
        light: ensureStringColor(themePrimary.light, '#60E7EB'),
        main: ensureStringColor(themePrimary.main, '#039CBD'),
        dark: ensureStringColor(themePrimary.dark, '#015B88'),
        darker: ensureStringColor(themePrimary.darker, '#002F5A'),
        contrastText: ensureStringColor(themePrimary.contrastText, '#fff')
      };
    }
    return setColor;
  }, [activeTheme, setColor]);

  const themeOptions = useMemo(() => {
    // Ensure typography.fontSize is a number if present
    const typography = { ...defaultTheme.typography };
    if (typography.fontSize !== undefined) {
      if (Array.isArray(typography.fontSize)) {
        typography.fontSize = typography.fontSize[0];
      }
      if (typeof typography.fontSize !== 'number') {
        typography.fontSize = 14;
      }
    }

    // Ensure shape.borderRadius is a number if present
    const shape = { ...defaultTheme.shape };
    if (shape.borderRadius !== undefined) {
      if (Array.isArray(shape.borderRadius)) {
        shape.borderRadius = shape.borderRadius[0];
      }
      if (typeof shape.borderRadius !== 'number') {
        shape.borderRadius = 8;
      }
    }

    return {
      ...defaultTheme,
      typography,
      shape,
      palette: {
        ...defaultTheme.palette,
        primary: primaryColor
      },
      customShadows: {
        ...defaultTheme.customShadows,
        primary: `0 8px 16px 0 ${alpha(typeof primaryColor.main === 'string' ? primaryColor.main : (primaryColor.main || '#039CBD'), 0.24)}`
      }
    };
  }, [primaryColor, defaultTheme]);

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
